(ns assets-and-liabilities.views
  (:require
    [reagent.core :as r]
    [reagent.format :as fmt]
    [very.re-frame.core :as rf]
    [main.common :as common]
    [util.core :refer [long-str string->float]]
    [css.core :refer [defstyled get-theme]]
    [css.vars :as vars]
    [css.form :as form]
    [profile.common :as pc]
    [cljss.core :as cljss]))

(defonce tmp-count (atom 0))

(defn- nxt-tmp []
  (swap! tmp-count inc)
  (deref tmp-count))

(defn- empty-asset []
  {:id (str (random-uuid))
   :name ""
   :type ""
   :value ""
   :tmp? true
   :order (nxt-tmp)})

(defn- empty-liability []
  {:id (str (random-uuid))
   :name ""
   :type ""
   :value ""
   :tmp? true
   :order (nxt-tmp)})

(defstyled form-section-s :section
  {:margin-top "115px"
   ::cljss/media {[[:max-width "768px"]]
                  {:margin-top "55px"}}})

(defstyled form-section-header-s :div
  {:display "grid"
   :grid-template-columns "1fr max-content"
   :grid-template-rows "1fr 1fr"
   :align-items "center"
   :border-bottom (str "2px solid " (get-theme :primary))
   :padding-bottom "15px"
   :margin-bottom "45px"
   ::cljss/media {[[:max-width "768px"]]
                  {:grid-template-columns "1fr 1fr"
                   :grid-template-rows "1fr 1fr"
                   :margin-bottom "30px"}}})

(defstyled form-section-title-s :h2
  {:grid-column "span 2"
   :font-size "24px"
   :color "#282828"
   :font-weight "400"
   :margin-bottom "5px"
   ::cljss/media {[[:max-width "768px"]]
                  {:grid-column "1"
                   :font-size "20px"}}})

(defstyled form-section-description-s :div
  {:font-size "15px"
   :color vars/gray
   ::cljss/media {[[:max-width "768px"]]
                  {:grid-column "span 2"}}})

(defstyled form-section-total-s :h2
  {:font-size "24px"
   :color "#282828"
   :font-weight "400"
   ::cljss/media {[[:max-width "768px"]]
                  {:grid-column "2"
                   :grid-row "1"
                   :text-align "right"
                   :font-size "20px"}}})

(defstyled input-row-s :div
  {::cljss/media {[[:max-width "768px"]]
                  {:grid-column "span 2"
                   :margin-bottom "15px"}}})

(defstyled form-row-s :li
  {:display "grid"
   :grid-template-columns "1fr 1fr 1fr 0px"
   :grid-column-gap "30px"
   :margin-bottom "30px"
   ::cljss/media {[[:max-width "768px"]]
                  {:grid-template-columns "1fr 1fr"
                   :align-items "flex-start"}}})

(defstyled order-number-s :div
  {:display "none"
   ::cljss/media {[[:max-width "768px"]]
                  {:display "block"
                   :grid-column "1"
                   :margin-bottom "15px"
                   :font-size "18px"
                   :font-weight "bold"}}})

(defstyled assets-s :ul {})

(defstyled liabilities-s :ul {})

(defn manage-assets-liabilities [{:keys [on-save on-cancel cancel-label] :or {cancel-label "Cancel"}}]
  (let [as (rf/subscribe [:assets])
        as-total (rf/subscribe [:profile/assets-total])
        li (rf/subscribe [:liabilities])
        li-total (rf/subscribe [:profile/liabilities-total])

        asset-form (atom {})
        liability-form (atom {})

        tmp-assets (r/atom @as)
        add-asset (fn [] (swap! tmp-assets conj (empty-asset)))
        remove-asset (fn [a]
                       (swap! tmp-assets (fn [as] (remove #(= (:id a) (:id %)) as))))

        tmp-liabilities (r/atom @li)
        add-liability (fn [] (swap! tmp-liabilities conj (empty-liability)))
        remove-liability (fn [l]
                           (swap! tmp-liabilities (fn [li] (remove #(= (:id l) (:id %)) li))))

        get-form-values (fn [x form]
                          (reduce (fn [m [k f]]
                                    (assoc m k
                                           (if (contains? #{:value} k)
                                             (string->float (.-value f))
                                             (.-value f))))
                                  {}
                                  (get form (:id x))))

        gather (fn [e]
                 (.preventDefault e)
                 (let [assets (reduce (fn [c a] (conj c (get-form-values a @asset-form))) [] @tmp-assets)
                       liabilities (reduce (fn [c l] (conj c (get-form-values l @liability-form))) [] @tmp-liabilities)]
                   (on-save {:assets assets :liabilities liabilities})))
        submitting? (r/atom false)]
    (fn []
      [common/form-page-s
       [common/form-s {:on-submit #(do
                                     (reset! submitting? true)
                                     (gather %))
                       :auto-complete "off"}
        [common/form-header-s
         [common/form-title-s "Additional assets & liabilities"]
         [common/form-explain-s "Assets and liabilities that are not accounts or related to a financial institution"]]
        ;; this should be in its own fn but then we lose access to the form
        ;; inputs and stateless-ness
        [form-section-s
         [form-section-header-s
          [form-section-title-s "Assets"]
          [form-section-description-s "Real estate properties, businesses, valuables (jewelry, art), vehicles, other"]
          [pc/form-section-total-s "Total: " (fmt/currency-format @as-total)]]
         [assets-s
          (doall (for [a (sort-by #(when (contains? % :tmp?)
                                     (:order %)) @tmp-assets)]
                   ^{:key (:id a)}
                   [form-row-s
                    [order-number-s "Asset"]
                    [form/input-row-s {:margin "0"}
                     [form/input-label-s "Name"]
                     [form/input {:type "text"
                                  :label? true
                                  :ref #(swap! asset-form assoc-in [(:id a) :name] %)
                                  :name "name"
                                  :default-value (:name a)
                                  :required true}]]
                    [form/input-row-s {:margin "0"}
                     [form/select {:default-value (:type a)
                                   :name "type"
                                   :ref #(swap! asset-form assoc-in [(:id a) :type] %)
                                   :required true}
                      [:option {:disabled true
                                :value ""} "Type"]
                      [:option {:value "REAL_ESTATE_PROPERTY"} "Real estate property"]
                      [:option {:value "BUSINESS"} "Business"]
                      [:option {:value "VALUABLE"} "Valuable"]
                      [:option {:value "VEHICLE"} "Vehicle"]
                      [:option {:value "OTHER"} "Other"]]]
                    [form/input-row-s {:currency? true
                                       :margin "0"}
                     [form/input-label-s "Value"]
                     [form/input {:type "number"
                                  :name "value"
                                  :step 0.01
                                  :label? true
                                  :unit? true
                                  :min 0
                                  :ref #(swap! asset-form assoc-in [(:id a) :value] %)
                                  :default-value (:value a)
                                  :required true}]]
                    [form/remove-button {:on-click #(remove-asset a)
                                         :type "button"}]]))]
         [form/naked-button-s {:on-click add-asset
                               :type "button"}
          [form/naked-button-symbol-s "+"]
          [form/naked-button-text-s "New asset"]]]


        [form-section-s
         [form-section-header-s
          [form-section-title-s "Liabilities"]
          [form-section-description-s "Personal loans, mortgages, property loan, auto loan, student loan, other"]
          [pc/form-section-total-s "Total: " (fmt/currency-format (- @li-total))]]
         [liabilities-s
          (doall (for [l (sort-by #(when (contains? % :tmp?)
                                     (:order %)) @tmp-liabilities)]
                   ^{:key (:id l)}
                   [form-row-s
                    [order-number-s "Liability"]
                    [form/input-row-s {:margin "0"}
                     [form/input {:type "text"
                                  :name "name"
                                  :placeholder "Name"
                                  :ref #(swap! liability-form assoc-in [(:id l) :name] %)
                                  :default-value (:name l)
                                  :required true}]]
                    [form/input-row-s {:margin "0"}
                     [form/select {:default-value (:type l)
                                   :name "type"
                                   :ref #(swap! liability-form assoc-in [(:id l) :type] %)
                                   :required true}
                      [:option {:disabled true
                                :value ""} "Type"]
                      [:option {:value "PERSONAL_LOAN"} "Personal Loan"]
                      [:option {:value "MORTGAGE"} "Mortgage"]
                      [:option {:value "PROPERTY_LOAN"} "Property Loan"]
                      [:option {:value "AUTO_LOAN"} "Auto Loan"]
                      [:option {:value "STUDENT_LOAN"} "Student Loan"]
                      [:option {:value "OTHER"} "Other"]]]
                    [form/input-row-s {:margin "0"
                                       :currency? true}
                     [form/input-label-s "Debt"]
                     [form/input {:type "number"
                                  :step 0.01
                                  :label? true
                                  :unit? true
                                  :max -0.01
                                  :ref #(swap! liability-form assoc-in [(:id l) :value] %)
                                  :name "value"
                                  :default-value (:value l)
                                  :required true}]]
                    [form/remove-button {:on-click #(remove-liability l)
                                         :type "button"}]]))]
         [form/naked-button-s {:on-click add-liability
                               :type "button"}
          [form/naked-button-symbol-s "+"]
          [form/naked-button-text-s "New liability"]]]
        [form/button-row-s
         [form/secondary-button-s {:on-click on-cancel
                                   :disabled @submitting?
                                   :type "button"} cancel-label]
         [form/button {:type "submit"
                       :disabled @submitting?}
          (if @submitting? "Creating..." "Save")]]]])))

(defstyled assets-and-liabilities-s :section {})

(defn assets-liabilities [{:keys [on-edit]}]
  (let [assets (rf/subscribe [:assets])
        ass-total (rf/subscribe [:profile/assets-total])
        liabilities (rf/subscribe [:liabilities])
        lia-total (rf/subscribe [:profile/liabilities-total])]
    (fn []
      [assets-and-liabilities-s
       [common/profile-section-header {:link on-edit
                                       :text "Edit"
                                       :title "Additional assets and liabilities"}]
       (if (some not-empty [@assets @liabilities])
         [:div
          [common/data-row-full {:label "Total additional assets:"
                                 :value (fmt/currency-format @ass-total)}]
          [common/data-row-full {:label "Total additional liabilities:"
                                 :value (fmt/currency-format @lia-total)}]]
         [common/no-results
          {:text (long-str "Comprehensive planning begins with a thorough understanding of your current situation. "
                           "Complete this section to give us an idea of your resources and responsibilities.")
           :button-text "Enter your assets & liabilities"
           :button-click on-edit}])])))

